import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, InputBase, ListItem, ListItemButton, ListItemText, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";

import { makeStyles, useTheme } from "@mui/styles";

import uuid from "react-uuid";
import { useSnackbar } from "notistack";

import { FixedSizeList } from "react-window";

import { Search } from "@mui/icons-material";
import { selectEconomics } from "../reducers/economicsSlice";
import { addProject, selectProjects } from "../reducers/projectSlice";
import { selectUser } from "../reducers/userSlice";

const useStyles = makeStyles((theme) => ({
  search: {
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    marginBottom: 5,
    paddingLeft: 5,
    width: 300,
    // border: `1px solid ${theme.palette.primary.main}`,
    height: 36,
  },
  searchIcon: {
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // color: theme.palette.primary.main
  },
  inputRoot: {
    color: 'inherit'
  },
  inputInput: {
    // vertical padding + font size from searchIcon
    paddingLeft: 24
  }
}));

export const EconomicsMoveCopy = ({ economicsId, action, handleSaveEconomics, setEconomicsMoveCopyVisible }) => {
  
  const theme = useTheme();
  const classes = useStyles();

  const [economicsAction, setEconomicsAction] = useState(action || 'copy');
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [searchProjectName, setSearchProjectName] = useState("");

  const user = useSelector(selectUser);
  const darkMode = user?.value?.darkMode;
  const economics = useSelector(selectEconomics);
  const currentEconomics = economics?.byId?.[economicsId];

  const projects = useSelector(selectProjects);
  let projectIds = [...projects?.allIds] || [];
  projectIds = projectIds.filter(id => id !== currentEconomics?.projectId && projects?.byId?.[id]?.createdBy === user?.value?.email);
  if (searchProjectName?.length > 0) {
    projectIds = projectIds.filter(id => projects?.byId?.[id]?.name?.toLowerCase().includes(searchProjectName.toLowerCase()));
  };

  const dispatch = useDispatch();

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (economicsAction === 'move' && selectedProjectIds?.length > 1) {
      setSelectedProjectIds(prev => [prev[0]]);
    };
  }, [economicsAction, selectedProjectIds?.length]);

  return (
    <Dialog 
      open={true} 
      onClose={() => setEconomicsMoveCopyVisible(false)}
      fullWidth={true}
      maxWidth="sm"
      PaperProps={{
        style: {
          backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light']
        },
      }}
      elevation={0}
    >
      <DialogTitle>{economicsAction === 'copy' ? 'Copy' : 'Move'} Economics</DialogTitle>
      <DialogContent>
        
        <Typography variant="h5" style={{marginBottom: 10}}>Select action</Typography>
        <ToggleButtonGroup
          value={economicsAction}
          exclusive
          onChange={(e, val) => val && setEconomicsAction(val)}
          size="small"
          style={{height: 30, width: '100%', display: 'flex', justifyContent: 'center', marginBottom: 20}}
        >
          <ToggleButton value="copy" size="small" style={{width: '50%'}} color={(darkMode ? "" : "primary")}>
            <Typography style={{fontSize: 12}}>
              Copy
            </Typography>
          </ToggleButton>
          <ToggleButton value="move" size="small" style={{width: '50%'}} color={(darkMode ? "" : "primary")}>
            <Typography style={{fontSize: 12}}>
              Move
            </Typography>
          </ToggleButton>
        </ToggleButtonGroup>

        <Typography variant="h5" style={{marginTop: 40}}>Select a project</Typography>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 0, marginBottom: 10}}>
          
          <Typography variant="caption" color={'textSecondary'} style={{}}>Projects: {projectIds?.length || 0}</Typography>


          <div className={classes.search} style={{border: `1px solid ${darkMode ? "lightgrey" : theme.palette.primary.main}`}}>
            <div className={classes.searchIcon}>
              <Search color={darkMode ? "inherit" : "primary"} />
            </div>
            <InputBase
              placeholder="Search projects by name"
              classes={{
                root: classes.inputRoot,
                input: classes.inputInput,
              }}
              fullWidth
              value={searchProjectName}
              onChange={e => setSearchProjectName(e.target.value)}
            />
          </div>

        </div>

        <FixedSizeList
          height={400}
          width={'100%'}
          itemSize={48}
          itemCount={projectIds?.length || 0}
          // overscanCount={5}
          style={{border: '1px solid lightgrey', borderRadius: '5px', padding: 10}}
        >
          {renderProjectRow}
        </FixedSizeList>
        
      </DialogContent>
      <DialogActions style={{display: 'flex', justifyContent: 'flex-end'}}>
        <Button onClick={() => setEconomicsMoveCopyVisible(false)} color={darkMode ? "inherit" : "primary"} style={{marginRight: 10}}>
          Cancel
        </Button>
        <Button onClick={() => handleMoveCopyEconomics(selectedProjectIds)} color={darkMode ? "inherit" : "primary"}
          style={{color: darkMode ? '#000' : '#fff'}} variant="contained" disabled={selectedProjectIds?.length === 0}
        >
          {economicsAction === 'copy' ? 'Copy' : 'Move'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // FUNCTIONS
  function renderProjectRow(props) {
    const { index, style } = props;
    const checked = selectedProjectIds?.includes(projectIds?.[index]);
    const project = projects?.byId?.[projectIds?.[index]];
    return (
      <ListItem style={{...style, height: 48, padding: 0, borderBottom: `1px solid ${darkMode ? 'dimgrey': 'lightgrey'}`}} key={index} component="div" disablePadding selected={checked}>
        <ListItemButton style={{height: 48}} onClick={e => handleCheckClick(e, projectIds?.[index])}>
          <Checkbox checked={checked} style={{marginRight: 10, height: 40, width: 40, padding: 0}} onClick={e => handleCheckClick(e, projectIds?.[index])} />
          <ListItemText 
            primary={project?.name} 
            secondary={`Lists: ${project?.listIds?.length || 0} | FBAs: ${project?.fbaIds?.length || 0} | Economics: ${project?.economicsIds?.length || 0}`}
            secondaryTypographyProps={{style: {fontSize: 10}}}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  function handleCheckClick(e, projectId) {
    e.stopPropagation();
    e.preventDefault();

    if (economicsAction === 'copy') {
      if (selectedProjectIds?.includes(projectId)) {
        setSelectedProjectIds(selectedProjectIds.filter(id => id !== projectId));
      } else {
        setSelectedProjectIds([...selectedProjectIds, projectId]);
      };
    } else {
      setSelectedProjectIds([projectId]);
    };

  };

  function handleMoveCopyEconomics(ids) {
    try {
      
      for (let i = 0; i < ids?.length; i++) { // copy economics to each of the ids
        const projectId = ids?.[i];
        const newProject = {...projects?.byId?.[projectId]};
        const currentProject = {...projects?.byId?.[currentEconomics?.projectId]};

        const newEconomicsId = economicsAction === 'copy' ? uuid() : economicsId;

        newProject.economicsIds = [...newProject?.economicsIds, newEconomicsId];
        dispatch(addProject({ project: newProject, lists: [], email: user?.value?.email }));

        if (economicsAction === 'move') { // remove economics from current project
          currentProject.economicsIds = currentProject?.economicsIds?.filter(id => id !== economicsId);
          dispatch(addProject({ project: currentProject, lists: [], email: user?.value?.email }));
        };

        const newEconomics = {...currentEconomics, id: newEconomicsId, projectId};
        handleSaveEconomics(newEconomics);

      };
      
      enqueueSnackbar(`Economics ${economicsAction === 'copy' ? 'copied' : 'moved'} to ${ids?.length} projects`, {variant: 'success'});
      setEconomicsMoveCopyVisible(false);
      
    } catch (error) {
      console.log(error);
    };
  };

};